// @flow
import * as R from "ramda"

import { get } from "common/utils/get"
import set from "common/utils/set"
import type { CheckFlightsApiOutput } from "../../../booking/services/reservation/apis/types/checkFlightsApiTypes"
import type {
  Flight,
  Flights,
  JourneyInput,
  SupportedJourneyHiddenTypes,
} from "common/services/journey/types"

// We need to have possibility to inform users about hidden_flights
// Hidden flights are used in particular search combination
// For now the only cases we must communicate are return direct flights (marked as one-way)
// ===

export const SUPPORTED_JOURNEY_HIDDEN_TYPES = {
  // One Way: Direct
  // (from return made one way)
  // A -> B USED
  // B -> A throw away
  ONE_WAY_DIRECT: "ONE_WAY_DIRECT",

  // One Way: Indirect
  // (from return made one way)
  // A -> B USED
  // B -> C throw away
  // C -> D throw away
  // ......   throw away
  // X -> A throw away
  ONE_WAY_INDIRECT: "ONE_WAY_INDIRECT",

  // Return: Direct
  // (aka cross)
  // A1 -> B1, B1 -> A1
  // A2 -> B2, B2 -> A2
  // A1 -> B1, B2 -> A2 + vice versa
  RETURN_DIRECT: "RETURN_DIRECT",

  // Return: Indirect
  // A -> B USED
  // B -> A throw away
  // ...... throw away
  // C -> D USED
  // D -> C throw away
  // ...... throw away
}

// For sorting flights by departure time
const flightsDepartureTimeComparator = (a: Flight, b: Flight): number => a.dtime_utc - b.dtime_utc

const flightsFormReturnTrip = (flights: Flights): boolean =>
  flights.length === 2 && flights[0].src === flights[1].dst && flights[0].dst === flights[1].src

export const determineTypeOfHiddenFlights = (flights: Flights): SupportedJourneyHiddenTypes => {
  const validFlights = flights.filter(x => !x.is_hidden).sort(flightsDepartureTimeComparator)
  const hiddenFlights = flights.filter(x => x.is_hidden).sort(flightsDepartureTimeComparator)

  const validFlightsCount = validFlights.length
  const hiddenFlightsCount = hiddenFlights.length
  if (validFlightsCount === 0 || hiddenFlightsCount === 0) return null

  if (validFlightsCount === 1 && hiddenFlightsCount === 1)
    return SUPPORTED_JOURNEY_HIDDEN_TYPES.ONE_WAY_DIRECT

  // FIXME: Better
  // Hidden flights form regular trip (aka segment)
  // https://monosnap.com/file/CWSlToul2fVIAXeDra5BbBb3xMjYNp
  if (validFlightsCount === 2 && flightsFormReturnTrip(hiddenFlights)) {
    return SUPPORTED_JOURNEY_HIDDEN_TYPES.RETURN_DIRECT
  }

  if (
    (validFlightsCount >= 1 && hiddenFlightsCount > 1) ||
    (flights.length === 3 && validFlightsCount >= 1 && hiddenFlightsCount >= 1)
  )
    return SUPPORTED_JOURNEY_HIDDEN_TYPES.ONE_WAY_INDIRECT

  return null
}

// Combine valid and hidden flights in correct order for FE usage
const flightsCombiner = (flights: Flights, type: SupportedJourneyHiddenTypes): Flights => {
  const flightsSorted = flights.sort(flightsDepartureTimeComparator)
  const validFlights = flightsSorted.filter(x => !x.is_hidden)
  const hiddenFlights = flightsSorted.filter(x => x.is_hidden)

  switch (type) {
    case SUPPORTED_JOURNEY_HIDDEN_TYPES.ONE_WAY_INDIRECT:
    case SUPPORTED_JOURNEY_HIDDEN_TYPES.ONE_WAY_DIRECT:
      return [...validFlights, ...hiddenFlights.map(set("return", 1))]
    case SUPPORTED_JOURNEY_HIDDEN_TYPES.RETURN_DIRECT:
      return [...flightsSorted.filter(x => !x.return), ...flightsSorted.filter(x => x.return)]
    default:
      return flights
  }
}

export const getFlightsFromInputSafe = (input: JourneyInput): Flights =>
  input.flights || input.route || []

export const hasHiddenFlight = (input: JourneyInput | CheckFlightsApiOutput): boolean =>
  R.compose(
    R.any(Boolean),
    R.map(get("is_hidden")),
    getFlightsFromInputSafe,
    // $FlowIssue
  )(input)

export const filterHiddenFlightOut = (rawInput: JourneyInput): JourneyInput => {
  const input = { ...rawInput }

  if (input.flights) {
    return set("flights", input.flights.filter(flight => !flight.is_hidden), input)
  }
  if (input.route) {
    return set("route", input.route.filter(flight => !flight.is_hidden), input)
  }

  return input
}

export const hiddenFlightFormattingSupported = (input: JourneyInput): boolean => {
  const flights = getFlightsFromInputSafe(input)
  return Boolean(determineTypeOfHiddenFlights(flights))
}

// Label second flight as return to simulate correct appearance
export const formatForHiddenFlightsMapping = (rawInput: JourneyInput): JourneyInput => {
  const input = { ...rawInput }
  const flights = getFlightsFromInputSafe(input)
  const flightsParamName = input.flights ? "flights" : "route"
  const hiddenFlightsType = determineTypeOfHiddenFlights(flights)

  return R.compose(
    set("hiddenFlightsType", hiddenFlightsType),
    set([flightsParamName], flightsCombiner(flights, hiddenFlightsType)),
  )(input)
}
